import type { CreatePlayerRequest, PatchTournamentRequest, TournamentResult } from '@brush/api-client';

/**
 * The writes this app sends about one tournament, each refused before anything is sent when the tournament
 * belongs to a room other than the one on screen (#591).
 *
 * A tournament's PATCH and DELETE name it by id alone, and the server checks the tournament's OWN room —
 * which an administrator, or an operator of both rooms, may act in. So the server cannot tell a tournament
 * edited from its own room's screen from one edited from a screen left open across a room switch, whose
 * header now names another room. The room key in `App.tsx` closes every such screen on a switch; this is
 * the check at the moment of sending, and it is what `tournamentWrites.test.js` drives with a recording API.
 *
 * The room on screen is the hook's `roomId`, read when the write is made, never a value captured earlier.
 */

// A tournament PATCH must carry every field; null means "leave unchanged".
//
// clearGuarantee is the exception and defaults to false: it is the one field where "not
// mentioned" and "set to nothing" needed telling apart, since null was already taken.
export const EMPTY_TOURNAMENT_PATCH: PatchTournamentRequest = {
  internalId: null,
  name: null,
  date: null,
  time: null,
  buyIn: null,
  guarantee: null,
  clearGuarantee: false,
  guaranteeUnits: null,
  eventNumber: null,
  isMultiDay: null,
  sessions: null,
  status: null,
  tags: null,
  results: null,
};

/** The part of the API client these writes use. */
export interface TournamentWriteApi {
  tournaments: {
    patch: (id: string, body: PatchTournamentRequest) => Promise<unknown>;
    remove: (id: string) => Promise<unknown>;
  };
  players: { create: (body: CreatePlayerRequest) => Promise<{ id: string }> };
}

/** A tournament as a write names it: by id, and the room it belongs to. */
export interface TournamentTarget {
  id: string;
  roomId: string;
}

export interface ResultsImportOutcome {
  playersCreated: number;
  resultsRecorded: number;
  /** Set when the run stopped. Results are all-or-nothing, so this means none were written. */
  failure: string | null;
}

export class OtherRoomError extends Error {
  constructor() {
    super('This tournament belongs to another room than the one on screen, so nothing was sent. Open it from its own room.');
    this.name = 'OtherRoomError';
  }
}

/** Refuses a write about a room that is not the one on screen, or when no room is. */
export function ensureOnScreen(onScreen: string | null, roomId: string): void {
  if (onScreen === null || roomId !== onScreen) throw new OtherRoomError();
}

export async function patchTournament(
  api: TournamentWriteApi, onScreen: string | null, target: TournamentTarget, body: Partial<PatchTournamentRequest>,
): Promise<unknown> {
  ensureOnScreen(onScreen, target.roomId);
  return api.tournaments.patch(target.id, { ...EMPTY_TOURNAMENT_PATCH, ...body });
}

export async function deleteTournament(
  api: TournamentWriteApi, onScreen: string | null, target: TournamentTarget,
): Promise<unknown> {
  ensureOnScreen(onScreen, target.roomId);
  return api.tournaments.remove(target.id);
}

/**
 * Importing a file of finishing positions into one tournament.
 *
 * Two phases, in this order for the same reason the manual editor uses it: a player created
 * without their result is a harmless record the room can use again, while a result pointing at a
 * player id that does not exist is a foreign key violation. A failed create therefore stops the
 * run before any result is written. The room is checked before either: the players are created in
 * the room on screen, so a tournament of another room would refuse their results and leave them behind.
 *
 * The results themselves go in a single PATCH, so unlike the tournament import there is no
 * partial state to report — either the tournament ends up as the file describes it or it is
 * untouched.
 */
export async function importResults(
  api: TournamentWriteApi,
  onScreen: string | null,
  { target, newPlayers, build, onProgress, describeError }: {
    target: TournamentTarget;
    newPlayers: CreatePlayerRequest[];
    /** Turns card-id → player-id into the results to write, once every player exists. */
    build: (idsByCard: Map<string, string>) => TournamentResult[];
    onProgress?: (done: number) => void;
    /** `describeApiError`, passed in so this module carries no value import a test runner cannot resolve. */
    describeError: (err: unknown) => string | null;
  },
): Promise<ResultsImportOutcome> {
  try {
    ensureOnScreen(onScreen, target.roomId);
  } catch (err) {
    return { playersCreated: 0, resultsRecorded: 0, failure: (err as Error).message };
  }

  const idsByCard = new Map<string, string>();
  let playersCreated = 0;

  for (const body of newPlayers) {
    try {
      const player = await api.players.create(body);
      // Keyed on the card because that is what the file rows carry — the id did not exist
      // until this call returned.
      idsByCard.set(body.cardId.toLowerCase(), player.id);
      playersCreated += 1;
    } catch (err) {
      return {
        playersCreated,
        resultsRecorded: 0,
        failure:
          `${describeError(err) ?? `Couldn't create the player on card ${body.cardId}.`} ` +
          'No results were recorded.',
      };
    }
    onProgress?.(playersCreated);
  }

  const results = build(idsByCard);
  try {
    await api.tournaments.patch(target.id, { ...EMPTY_TOURNAMENT_PATCH, results });
  } catch (err) {
    return {
      playersCreated,
      resultsRecorded: 0,
      failure: describeError(err) ?? 'The results were rejected.',
    };
  }

  onProgress?.(newPlayers.length + 1);
  return { playersCreated, resultsRecorded: results.length, failure: null };
}

/**
 * The sentence for a write refused here. `describeApiError` reads any error that is not the API's as a
 * failure to reach the server and says to check the connection, which this is not — nothing was sent.
 */
export function writeRefusal(error: unknown): string | null {
  return error instanceof OtherRoomError ? error.message : null;
}
